/**
 * 角色卡页（T07）。
 * -----------------------------------------------------------------------------
 * 一个 hash 路由下承载两个视图（列表 / 编辑），用 pageTransition 做物理页面切换：
 *   - 列表：角色卡增删改查 + 导入 / 导出（删除二次确认）；
 *   - 编辑：字段编辑 + 世界书子编辑器。
 *
 * 视图切换用 `AnimatePresence + pageTransition`（前进从右推入、返回从左推入），
 * 列表入场用 listStagger，按压用 tap —— 动效取值全部来自 src/design/motion.ts。
 * ============================================================================= */

import { AnimatePresence, motion } from 'framer-motion';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ChangeEvent, ReactElement } from 'react';

import { navigate } from '../../app/hashRoute';
import { newId } from '../../core/ids';
import * as presetRepo from '../../core/repositories/presetRepo';
import type { Preset } from '../../core/types';
import { useMotionPresets } from '../../design/motion';
import type { PageDirection } from '../../design/motion';
import { CHARACTER_IMPORT_ACCEPT, readPresetImportFile, WORLD_BOOK_IMPORT_ACCEPT } from './importFile';
import { validQuietPeriod } from '../automation/quietHours';
import { getEntitlements } from '../subscription/entitlements';
import { SERVER_PROVIDER_ID } from '../../core/providers';
import { loadTtsCatalog } from '../mimo/catalog';
import { roleVoiceError, roleVoiceMode, roleVoiceProviderError, roleVoiceStore } from '../mimo/roleVoiceStore';
import { getMomentAccountId } from '../moments/accountScope';
import { AUTOMATION_SYNC_STATUS_EVENT, getAutomationSyncError, savePresetWithAutomationSync } from '../moments/cloudSync';
import { PresetEditorView } from './PresetEditorView';
import { PresetListView } from './PresetListView';
import { parseImportPresetJson, parseWorldBookImportJson } from './importFormats';
import { presetFileName, serializePreset } from './presetTransfer';
import './presets.css';

type View =
  | { kind: 'list' }
  | { kind: 'edit'; draft: Preset; isNew: boolean; modelInitiallyOpen?: boolean };

/** 触发浏览器下载一份 JSON 文本（导出角色卡）。 */
function downloadTextFile(filename: string, text: string): void {
  const blob = new Blob([text], { type: 'application/json;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.style.display = 'none';
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

export function PresetsPage(): ReactElement {
  const motionPresets = useMotionPresets();
  const [presets, setPresets] = useState<Preset[]>([]);
  const [view, setView] = useState<View>({ kind: 'list' });
  const [direction, setDirection] = useState<PageDirection>('forward');
  const [notice, setNotice] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<Preset | null>(null);
  const [editorError, setEditorError] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [syncError, setSyncError] = useState(getAutomationSyncError);
  const saveBusy = useRef(false);
  const importBusy = useRef(false);
  const currentView = useRef(view);
  currentView.current = view;
  const cardInputRef = useRef<HTMLInputElement | null>(null);
  const worldBookInputRef = useRef<HTMLInputElement | null>(null);
  const returnToChat = useRef(false);

  const reload = useCallback(async (): Promise<void> => {
    setPresets(await presetRepo.listPresets());
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  useEffect(() => {
    let active = true;
    const openLinkedRole = (): void => {
      const query = new URLSearchParams(window.location.hash.split('?')[1] ?? '');
      const id = query.get('edit');
      if (!id) return;
      void presetRepo.getPreset(id).then(preset => {
        if (!active || !preset) return;
        returnToChat.current = query.get('return') === 'chat';
        setEditorError(null); setNotice(null); setDirection('forward');
        setView({ kind: 'edit', draft: { ...preset }, isNew: false, modelInitiallyOpen: query.get('model') === '1' });
      });
    };
    openLinkedRole();
    window.addEventListener('hashchange', openLinkedRole);
    return () => { active = false; window.removeEventListener('hashchange', openLinkedRole); };
  }, []);

  useEffect(() => {
    const update = () => setSyncError(getAutomationSyncError());
    window.addEventListener(AUTOMATION_SYNC_STATUS_EVENT, update);
    window.addEventListener('zhixin:account-change', update);
    return () => {
      window.removeEventListener(AUTOMATION_SYNC_STATUS_EVENT, update);
      window.removeEventListener('zhixin:account-change', update);
    };
  }, []);

  const backToList = useCallback((): void => {
    if (returnToChat.current) { navigate('/chat'); return; }
    setDirection('backward');
    setEditorError(null);
    setNotice(null);
    setView({ kind: 'list' });
  }, []);

  const openCreate = (): void => {
    returnToChat.current = false;
    setNotice(null);
    setEditorError(null);
    setDirection('forward');
    setView({
      kind: 'edit',
      draft: {
        ...presetRepo.createPreset({ name: '', systemPrompt: '' }),
        automationMode: 'reply_only',
        autoImageEnabled: false,
        autoTtsEnabled: false,
        quietPeriods: [],
      },
      isNew: true,
    });
  };

  const openEdit = (preset: Preset): void => {
    returnToChat.current = false;
    setNotice(null);
    setEditorError(null);
    setDirection('forward');
    setView({ kind: 'edit', draft: { ...preset }, isNew: false });
  };

  const saveDraft = async (draft: Preset): Promise<void> => {
    if (saveBusy.current) return;
    setEditorError(null);
    if (draft.name.trim() === '') {
      setEditorError('名称不能为空');
      return;
    }
    const entitlements = getEntitlements();
    if (!entitlements.capabilities.autoImage) draft.autoImageEnabled = false;
    if (!entitlements.capabilities.autoTts) draft.autoTtsEnabled = false;
    if ((draft.automationMode ?? 'reply_only') !== 'reply_only' && !entitlements.capabilities.autoText) {
      setEditorError('当前套餐不支持自动冒泡或自动朋友圈');
      return;
    }
    if (draft.autoImageEnabled === true) {
      if (!entitlements.capabilities.autoImage) {
        setEditorError('当前套餐不支持自动生图');
        return;
      }
      if (!draft.imageProviderId || !draft.imageModel?.trim()) {
        setEditorError('开启自动生图后必须选择生图提供商和模型');
        return;
      }
    }
    if (draft.autoTtsEnabled === true) {
      if (!entitlements.capabilities.autoTts) {
        setEditorError('当前套餐不支持自动语音');
        return;
      }
      if (!draft.ttsProviderId || !draft.ttsModel?.trim()) {
        setEditorError('开启自动语音后必须选择语音提供商和模型');
        return;
      }
    }
    if ((draft.quietPeriods ?? []).some((period) => !validQuietPeriod(period))) {
      setEditorError('勿扰时段的起止时间无效');
      return;
    }
    if (draft.ttsProviderId && draft.ttsModel) {
      try {
        const selectedMode = draft.ttsProviderId === SERVER_PROVIDER_ID && !draft.ttsVoiceMode
          ? (await loadTtsCatalog()).find(model => model.id === draft.ttsModel)?.mode ?? roleVoiceMode(draft)
          : roleVoiceMode(draft);
        const providerError = roleVoiceProviderError({ ...draft, ttsVoiceMode: selectedMode });
        if (providerError) { setEditorError(providerError); return; }
        const profile = await roleVoiceStore.read(getMomentAccountId(), draft.id);
        const configurationError = roleVoiceError(selectedMode, profile, draft.ttsAutoVoiceDesign === true);
        if (configurationError) { setEditorError(configurationError); return; }
        draft.ttsVoiceMode = selectedMode;
      } catch {
        setEditorError('无法验证角色专属音色配置，请稍后重试');
        return;
      }
    }
    saveBusy.current = true;
    setSaving(true);
    try {
      await savePresetWithAutomationSync({ ...draft, name: draft.name.trim() });
      await reload();
      backToList();
    } catch (error) {
      const reason = error instanceof Error ? error.message : '请稍后重试';
      setEditorError(`保存失败：${reason}`);
    } finally {
      saveBusy.current = false;
      setSaving(false);
    }
  };

  const duplicate = async (preset: Preset): Promise<void> => {
    await presetRepo.putPreset({ ...preset, id: newId(), name: `${preset.name} 副本` });
    await reload();
  };

  const exportPreset = (preset: Preset): void => {
    try {
      downloadTextFile(presetFileName(preset), serializePreset(preset));
      setNotice(`已导出「${preset.name}」`);
    } catch (error) {
      setNotice(`导出失败：${error instanceof Error ? error.message : String(error)}`);
    }
  };

  const importFile = async (file: File, mode: 'card' | 'worldBook'): Promise<void> => {
    if (importBusy.current) return;
    importBusy.current = true;
    setImporting(true);
    setNotice(null);
    const target = view.kind === 'edit' && mode === 'worldBook' ? view.draft.id : undefined;
    try {
      const { text, avatar } = await readPresetImportFile(file);
      const warnings: string[] = [];
      if (target !== undefined) {
        const entries = parseWorldBookImportJson(text, file.name, warnings);
        const latest = currentView.current;
        if (latest.kind !== 'edit' || latest.draft.id !== target) return;
        setView(current => {
          if (current.kind !== 'edit' || current.draft.id !== target) return current;
          const existingIds = new Set((current.draft.worldBook ?? []).map(entry => entry.id));
          const imported = entries.map(entry => {
            const id = existingIds.has(entry.id) ? newId() : entry.id;
            existingIds.add(id);
            return { ...entry, id };
          });
          return { ...current, draft: { ...current.draft, worldBook: [...(current.draft.worldBook ?? []), ...imported] } };
        });
        setNotice([`已导入 ${entries.length} 条，保存后生效${warnings.length ? '；部分高级规则按本应用逻辑处理' : ''}。`, ...warnings].join('\n'));
        return;
      }
      const preset = parseImportPresetJson(text, file.name, warnings);
      if (mode === 'card' && preset.tags?.includes('导入世界书')) throw new Error('这是世界书文件，请使用「导入世界书」入口');
      if (mode === 'worldBook') preset.worldBook = parseWorldBookImportJson(text, file.name, warnings);
      if (avatar !== undefined) preset.avatar = avatar;
      if (await presetRepo.getPreset(preset.id)) preset.id = newId();
      await presetRepo.putPreset(preset);
      await reload();
      setNotice([`${mode === 'card' ? `已导入角色卡「${preset.name}」` : `已导入世界书「${preset.name}」`}${warnings.length ? '；部分高级规则按本应用逻辑处理' : ''}`, ...warnings].join('\n'));
    } catch (error) {
      if (target === undefined || (currentView.current.kind === 'edit' && currentView.current.draft.id === target)) {
        setNotice(error instanceof Error ? error.message : String(error));
      }
    } finally {
      importBusy.current = false;
      setImporting(false);
    }
  };

  const confirmDelete = async (): Promise<void> => {
    if (pendingDelete === null) return;
    await roleVoiceStore.remove(getMomentAccountId(), pendingDelete.id);
    await presetRepo.deletePreset(pendingDelete.id);
    setPendingDelete(null);
    await reload();
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>, mode: 'card' | 'worldBook'): void => {
    const file = event.target.files?.[0];
    if (file !== undefined) void importFile(file, mode);
    event.target.value = '';
  };

  const viewKey = view.kind === 'list' ? 'presets-list' : `presets-edit-${view.draft.id}`;

  return (
    <>
      {/* 隐藏的文件选择器（不可见部件，由工具栏「导入」按钮触发） */}
      <input
        ref={cardInputRef}
        type="file"
        accept={CHARACTER_IMPORT_ACCEPT}
        data-testid="presets-import-card-input"
        style={{ display: 'none' }}
        onChange={(event) => handleFileChange(event, 'card')}
      />
      <input
        ref={worldBookInputRef}
        type="file"
        accept={WORLD_BOOK_IMPORT_ACCEPT}
        data-testid="presets-import-worldbook-input"
        style={{ display: 'none' }}
        onChange={(event) => handleFileChange(event, 'worldBook')}
      />

      <AnimatePresence mode="wait" initial={false} custom={direction}>
        <motion.div
          key={viewKey}
          className="zs-view"
          custom={direction}
          variants={motionPresets.pageTransition}
          initial="enter"
          animate="center"
          exit="exit"
        >
          {view.kind === 'list' ? (
            <PresetListView
              presets={presets}
              importError={notice ?? (syncError ? `自动化配置未同步：${syncError}` : null)}
              pendingDelete={pendingDelete}
              importing={importing}
              onCreate={openCreate}
              onImportCardClick={() => {
                cardInputRef.current?.click();
              }}
              onImportWorldBookClick={() => {
                worldBookInputRef.current?.click();
              }}
              onOpenCommunity={() => navigate('/community')}
              onEdit={openEdit}
              onDuplicate={(preset) => {
                void duplicate(preset);
              }}
              onExport={exportPreset}
              onRequestDelete={(preset) => {
                setPendingDelete(preset);
              }}
              onCancelDelete={() => {
                setPendingDelete(null);
              }}
              onConfirmDelete={() => {
                void confirmDelete();
              }}
            />
          ) : (
            <PresetEditorView
              draft={view.draft}
              isNew={view.isNew}
              error={editorError}
              saving={saving}
              initialModelOpen={view.modelInitiallyOpen}
              notice={notice}
              importing={importing}
              onChange={(next) => {
                setNotice(null);
                setView({ kind: 'edit', draft: next, isNew: view.isNew });
              }}
              onSave={() => {
                void saveDraft(view.draft);
              }}
              onCancel={backToList}
              onImportWorldBook={() => {
                worldBookInputRef.current?.click();
              }}
            />
          )}
        </motion.div>
      </AnimatePresence>
    </>
  );
}
